import { useState, ReactNode } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical, Star } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import FilesTab from '../components/FilesTab'
import {
  Field,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  extractErr,
  Dash,
  InlineText,
  InlineDate,
  InlineSelect,
  InlineStatus,
  RecordDrawer,
  useDeleteConfirm,
  RowActions,
} from '@/components/ui/inline-edit'
import { DatePicker } from '@/components/DatePicker'

// ─────────────────── Types ───────────────────
type Account = { id: number; name: string }

type Contact = {
  id: number
  first_name: string
  last_name: string
  full_name: string
  email: string
  phone: string
  mobile: string
  job_title: string
  account: number | null
  account_name: string | null
  is_primary: boolean
  owner: number | null
  owner_username: string | null
  notes: string
  is_active: boolean
}

type Activity = {
  id: number
  activity_type: 'CALL' | 'EMAIL' | 'MEETING' | 'NOTE' | 'TASK'
  subject: string
  description: string
  related_contact: number | null
  owner: number | null
  owner_username: string | null
  status: 'PENDING' | 'COMPLETED' | 'CANCELLED'
  due_date: string | null
  completed_at: string | null
  duration_minutes: number | null
  is_overdue: boolean
}

// ─────────────────── Constants ───────────────────
const ACTIVE_COLOR: Record<'ACTIVE' | 'INACTIVE', string> = {
  ACTIVE: 'bg-green-100 text-green-800',
  INACTIVE: 'bg-gray-100 text-gray-600',
}

const ACTIVITY_STATUS_COLOR: Record<Activity['status'], string> = {
  PENDING: 'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-gray-100 text-gray-600',
}

const ACTIVITY_TYPE_LABEL: Record<Activity['activity_type'], string> = {
  CALL: 'Call', EMAIL: 'Email', MEETING: 'Meeting', NOTE: 'Note', TASK: 'Task',
}

type TabKey = 'details' | 'activities' | 'files'

// ─────────────────── Page ───────────────────
export default function CRMContactDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const contactId = id ? Number(id) : 0
  const { confirm, dialog: deleteDialog } = useDeleteConfirm()

  const contactQ = useQuery({
    queryKey: ['crm', 'contact', contactId],
    queryFn: async () => (await api.get<Contact>(`/crm/contacts/${contactId}/`)).data,
    enabled: !!contactId,
  })
  const accountsQ = useQuery({
    queryKey: ['crm', 'accounts'],
    queryFn: async () => (await api.get<Account[]>('/crm/accounts/')).data,
  })

  const updateMut = useMutation({
    mutationFn: async (payload: Partial<Contact>) =>
      (await api.patch(`/crm/contacts/${contactId}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm', 'contact', contactId] })
      qc.invalidateQueries({ queryKey: ['crm', 'contacts'] })
    },
  })

  const deleteMut = useMutation({
    mutationFn: async () => api.delete(`/crm/contacts/${contactId}/`),
    onSuccess: () => {
      toast.success('Contact deleted')
      qc.invalidateQueries({ queryKey: ['crm', 'contacts'] })
      navigate('/admin/crm/contacts')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const [tab, setTab] = useState<TabKey>('details')

  if (contactQ.isLoading) {
    return <AdminLayout><p className="text-muted-foreground">Loading…</p></AdminLayout>
  }
  if (contactQ.isError || !contactQ.data) {
    return (
      <AdminLayout>
        <div className="bg-card rounded-lg shadow p-8 text-center">
          <p className="text-foreground mb-4">Contact not found.</p>
          <Link to="/admin/crm/contacts" className="text-blue-600 hover:underline">← Back to contacts</Link>
        </div>
      </AdminLayout>
    )
  }

  const contact = contactQ.data
  const accounts = accountsQ.data || []

  const save = (payload: Partial<Contact>) =>
    updateMut.mutateAsync(payload).then(() => undefined)

  return (
    <AdminLayout>
      {deleteDialog}

      {/* Header */}
      <div className="mb-4">
        <div className="text-xs">
          <Link to="/admin/crm/contacts" className="text-muted-foreground hover:text-foreground hover:underline">← Contacts</Link>
        </div>
        <div className="flex items-start justify-between mt-2">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
              {contact.full_name || <span className="italic text-muted-foreground">Unnamed</span>}
              <button
                type="button"
                onClick={() => save({ is_primary: !contact.is_primary })}
                title={contact.is_primary ? 'Primary contact' : 'Mark as primary'}
                className="p-1 rounded hover:bg-muted"
              >
                <Star className={`h-5 w-5 ${contact.is_primary ? 'fill-amber-400 text-amber-500' : 'text-muted-foreground'}`} />
              </button>
            </h2>
            <div className="text-sm text-muted-foreground">
              {contact.job_title && <span>{contact.job_title}</span>}
              {contact.job_title && contact.account_name && <span> @ </span>}
              {contact.account && contact.account_name && (
                <Link to={`/admin/crm/accounts/${contact.account}`} className="hover:underline">
                  {contact.account_name}
                </Link>
              )}
              {contact.email && (
                <>
                  {(contact.job_title || contact.account_name) && <span> · </span>}
                  <a href={`mailto:${contact.email}`} className="hover:underline">{contact.email}</a>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <InlineStatus
              value={contact.is_active ? 'ACTIVE' : 'INACTIVE'}
              colorMap={ACTIVE_COLOR}
              options={[
                { value: 'ACTIVE', label: 'Active' },
                { value: 'INACTIVE', label: 'Inactive' },
              ]}
              onSave={(v) => save({ is_active: v === 'ACTIVE' })}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => confirm(
                    `contact ${contact.full_name}`,
                    () => deleteMut.mutate(),
                  )}
                  className="text-destructive"
                >
                  Delete contact
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="border-b mb-6">
        <nav className="flex gap-1 text-sm">
          {(['details', 'activities', 'files'] as TabKey[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 -mb-px border-b-2 ${
                tab === t ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'details' && 'Details'}
              {t === 'activities' && 'Activities'}
              {t === 'files' && 'Files'}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'details' && <DetailsTab contact={contact} accounts={accounts} onSave={save} />}
      {tab === 'activities' && <ActivitiesTab contactId={contact.id} contactName={contact.full_name} />}
      {tab === 'files' && <FilesTab parent="crm.contact" parentId={contactId} />}
    </AdminLayout>
  )
}

// ─────────────────── Details tab ───────────────────

function DetailsTab({
  contact, accounts, onSave,
}: {
  contact: Contact
  accounts: Account[]
  onSave: (payload: Partial<Contact>) => Promise<void>
}) {
  return (
    <div className="space-y-6 max-w-3xl">
      <Section title="Person">
        <Row label="First name">
          <InlineText value={contact.first_name} onSave={(v) => onSave({ first_name: v })} />
        </Row>
        <Row label="Last name">
          <InlineText value={contact.last_name} onSave={(v) => onSave({ last_name: v })} />
        </Row>
        <Row label="Job title">
          <InlineText value={contact.job_title} onSave={(v) => onSave({ job_title: v })} />
        </Row>
        <Row label="Account">
          <InlineSelect
            value={contact.account ? String(contact.account) : ''}
            displayLabel={contact.account_name || <Dash />}
            allowClear
            options={accounts.map((a) => ({
              value: String(a.id), label: a.name, searchText: a.name,
            }))}
            onSave={(v) => onSave({ account: v ? Number(v) : null })}
            helper="The account this contact works at. Leave empty for unaffiliated contacts."
          />
        </Row>
      </Section>

      <Section title="Contact">
        <Row label="Email">
          <InlineText
            value={contact.email}
            onSave={(v) => onSave({ email: v })}
            placeholder="name@company.com"
          />
        </Row>
        <Row label="Phone">
          <InlineText value={contact.phone} onSave={(v) => onSave({ phone: v })} />
        </Row>
        <Row label="Mobile">
          <InlineText value={contact.mobile} onSave={(v) => onSave({ mobile: v })} />
        </Row>
      </Section>

      <Section title="Notes">
        <Row label="Notes">
          <InlineText
            value={contact.notes}
            multiline
            onSave={(v) => onSave({ notes: v })}
            placeholder="Internal notes about this contact"
          />
        </Row>
      </Section>
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="bg-card rounded-lg shadow p-6">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">{title}</h3>
      <dl className="grid grid-cols-[180px_1fr] gap-x-6 gap-y-4 text-sm">
        {children}
      </dl>
    </section>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-1">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}

// ─────────────────── Activities tab ───────────────────

type ActivityDraft = {
  activity_type: Activity['activity_type']
  subject: string
  description: string
  due_date: string | null
  duration_minutes: number | null
  status: Activity['status']
}

function emptyActivityDraft(): ActivityDraft {
  return {
    activity_type: 'TASK', subject: '', description: '',
    due_date: null, duration_minutes: null, status: 'PENDING',
  }
}

function ActivitiesTab({ contactId, contactName }: { contactId: number; contactName: string }) {
  const qc = useQueryClient()
  const { confirm, dialog } = useDeleteConfirm()
  const activitiesQ = useQuery({
    queryKey: ['crm', 'activities', { contact: contactId }],
    queryFn: async () => (await api.get<Activity[]>(`/crm/activities/?contact=${contactId}`)).data,
  })

  const [editing, setEditing] = useState<Activity | null>(null)
  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<ActivityDraft>(emptyActivityDraft())

  function openCreate() {
    setEditing(null); setDraft(emptyActivityDraft()); setCreating(true)
  }
  function openEdit(a: Activity) {
    setCreating(false); setEditing(a)
    setDraft({
      activity_type: a.activity_type, subject: a.subject, description: a.description,
      due_date: a.due_date, duration_minutes: a.duration_minutes, status: a.status,
    })
  }
  function closeDrawer() { setCreating(false); setEditing(null) }

  const createMut = useMutation({
    mutationFn: async () => (await api.post('/crm/activities/', {
      ...draft,
      related_contact: contactId,
      related_lead: null, related_account: null, related_opportunity: null,
      owner: null,
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm', 'activities'] })
      toast.success('Activity added'); closeDrawer()
    },
    onError: (e) => toast.error(extractErr(e)),
  })
  const editMut = useMutation({
    mutationFn: async () => (await api.patch(`/crm/activities/${editing!.id}/`, draft)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm', 'activities'] })
      toast.success('Activity saved'); closeDrawer()
    },
    onError: (e) => toast.error(extractErr(e)),
  })
  const inlineMut = useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: Partial<Activity> }) =>
      (await api.patch(`/crm/activities/${id}/`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm', 'activities'] }),
  })
  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/crm/activities/${id}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm', 'activities'] })
      toast.success('Activity deleted')
    },
    onError: (e) => toast.error(extractErr(e)),
  })
  const saveField = async (id: number, payload: Partial<Activity>) => {
    await inlineMut.mutateAsync({ id, payload })
  }

  const activities = activitiesQ.data || []

  return (
    <div className="space-y-4">
      {dialog}
      <div className="flex items-start justify-between">
        <p className="text-xs text-muted-foreground max-w-2xl">
          Calls, emails, meetings, and tasks logged with this contact.
        </p>
        <Button size="sm" onClick={openCreate}><Plus className="h-3 w-3 mr-1" /> Add activity</Button>
      </div>

      {activitiesQ.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : activities.length === 0 ? (
        <p className="text-muted-foreground text-sm">No activities logged yet.</p>
      ) : (
        <table className="min-w-full divide-y divide-border text-sm">
          <thead className="bg-muted/40">
            <tr>
              <Th>Type</Th><Th>Subject</Th><Th>Due</Th><Th>Owner</Th><Th>Status</Th><Th />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {activities.map((a) => (
              <tr key={a.id} className={a.is_overdue && a.status === 'PENDING' ? 'bg-amber-50/50' : ''}>
                <Td className="text-xs font-semibold uppercase">{ACTIVITY_TYPE_LABEL[a.activity_type]}</Td>
                <Td className="font-medium">
                  <InlineText value={a.subject} onSave={(v) => saveField(a.id, { subject: v })} />
                </Td>
                <Td className="text-xs">
                  <InlineDate value={a.due_date} onSave={(v) => saveField(a.id, { due_date: v })} />
                  {a.is_overdue && a.status === 'PENDING' && (
                    <div className="text-xs text-amber-700 mt-0.5">overdue</div>
                  )}
                </Td>
                <Td className="text-xs">{a.owner_username || <Dash />}</Td>
                <Td>
                  <InlineStatus
                    value={a.status}
                    colorMap={ACTIVITY_STATUS_COLOR}
                    options={[
                      { value: 'PENDING', label: 'Pending' },
                      { value: 'COMPLETED', label: 'Completed' },
                      { value: 'CANCELLED', label: 'Cancelled' },
                    ]}
                    onSave={(v) => saveField(a.id, { status: v })}
                  />
                </Td>
                <Td className="text-right">
                  <RowActions
                    onEdit={() => openEdit(a)}
                    onDelete={() => confirm(
                      `${ACTIVITY_TYPE_LABEL[a.activity_type]} "${a.subject}"`,
                      () => deleteMut.mutate(a.id),
                    )}
                  />
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <RecordDrawer
        open={creating || editing !== null}
        onOpenChange={(v) => { if (!v) closeDrawer() }}
        title={editing ? 'Edit activity' : 'New activity'}
        description={editing
          ? 'Update this activity.'
          : `Log a call, email, meeting, note, or task with ${contactName}.`}
        onSubmit={() => editing ? editMut.mutate() : createMut.mutate()}
        isPending={createMut.isPending || editMut.isPending}
        submitLabel={editing ? 'Save' : 'Add activity'}
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="ac-type">Type</FieldLabel>
            <Select value={draft.activity_type}
              onValueChange={(v) => setDraft({ ...draft, activity_type: v as Activity['activity_type'] })}>
              <SelectTrigger id="ac-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(Object.keys(ACTIVITY_TYPE_LABEL) as Activity['activity_type'][]).map((k) => (
                  <SelectItem key={k} value={k}>{ACTIVITY_TYPE_LABEL[k]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field>
            <FieldLabel htmlFor="ac-subject">Subject *</FieldLabel>
            <Input id="ac-subject" autoFocus required value={draft.subject}
              onChange={(e) => setDraft({ ...draft, subject: e.target.value })} />
          </Field>
          <Field>
            <FieldLabel htmlFor="ac-desc">Description</FieldLabel>
            <Textarea id="ac-desc" rows={3} value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="ac-due">Due date</FieldLabel>
              <DatePicker id="ac-due" value={draft.due_date || ''}
                onChange={(v) => setDraft({ ...draft, due_date: v || null })} />
            </Field>
            <Field>
              <FieldLabel htmlFor="ac-duration">Duration (min)</FieldLabel>
              <Input id="ac-duration" type="number" min="0"
                value={draft.duration_minutes ?? ''}
                onChange={(e) => setDraft({
                  ...draft,
                  duration_minutes: e.target.value === '' ? null : Number(e.target.value),
                })}
                className="font-mono text-right" />
            </Field>
          </div>
          <Field>
            <FieldLabel htmlFor="ac-status">Status</FieldLabel>
            <Select value={draft.status}
              onValueChange={(v) => setDraft({ ...draft, status: v as Activity['status'] })}>
              <SelectTrigger id="ac-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="COMPLETED">Completed</SelectItem>
                <SelectItem value="CANCELLED">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </div>
  )
}

// ─────────────────── Helpers ───────────────────
function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-foreground ${className}`} onClick={onClick}>{children}</td>
}
